import { useCallback, useState } from 'react';
import { Layout, Card, Button, Spin } from 'antd';
import {
	FileSearchOutlined,
	FormOutlined,
	UserAddOutlined,
	TeamOutlined,
	SafetyOutlined,
	SettingOutlined,
	UserOutlined,
} from '@ant-design/icons';
import Icon from '../../components/ui-components/Icon';
import LogoutImage from '../../assets/images/logout-red-icon.svg';

import { useTranslation } from 'react-i18next';
import { useRedirectToLogin } from '../../hooks/useRedirectToLogin';

import {
	RoleBasedModules,
	UserRole,
} from '../../shared/VolunteerApplicationServiceTypes';

import { useGroupId } from '../../hooks/useGroupId';
import { usePreworkGroupId } from '../../hooks/usePreworkGroupId';
import useUserSession from '../../hooks/useUserSession';
import { useBatchId } from '../../hooks/useBatchId';

import {
	EXAM_MODULE_HOME,
	FUNCTIONARY_HOME,
	GROUPS,
	BATCH_HOME,
	ROOT,
	VIEW_FUNCTIONARIES,
	PREWORKGROUPS,
	MODULES_LIST,
} from '../../shared/Routes';

import {
	DisplayRole,
	GroupTabs,
	PreworkGroupTabs,
} from '../../shared/Constants';
import { getRolePrivilegeMappings } from '../../service/RoleBasedService';
import { displayError } from '../../shared/Utils';
import { useToastMessage } from '../../hooks/useToastMessage';
import { PrivilegeCodes } from '../../shared/RoleConstants';

type Module = {
	key: string;
	id: number;
	label: string;
	icon: React.ReactNode;
	onClick: () => void;
};

type StoredPrivilege = {
	privilege: {
		code: string;
	};
};

export const SelectModule = () => {
	const { t } = useTranslation();
	const { logout } = useRedirectToLogin();

	const { allRolesWithModules, selectedRole, setLoggedInUserPrivileges } =
		useUserSession();

	const [loadingModule, setLoadingModule] = useState<number | null>(null);

	const { navigateToRoute } = useBatchId();
	const { navigateWithGroupTab } = useGroupId();
	const { navigateWithPreworkBatchIdAndGroupTab } = usePreworkGroupId();
	const { showToastMessage } = useToastMessage();
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;

	const roleLabel = DisplayRole[selectedRole as keyof typeof DisplayRole];

	const handleModuleClickWithPrivileges = async (
		moduleId: number,
		onClick: () => void
	) => {
		try {
			if (!selectedRole) return;

			setLoadingModule(moduleId);

			const privilegesByRole = await getRolePrivilegeMappings({
				roleCode: selectedRole,
				moduleId,
				filters: {
					status: ['ACTIVE'],
					moduleId,
				},
			});
			const privilege = privilegesByRole.privileges || [];
			setLoggedInUserPrivileges(privilege);
			onClick();
			setLoadingModule(null);
		} catch (error) {
			const message = displayError(error);
			showToastMessage('error', message);
			setLoadingModule(null);
		}
	};

	const handleScreeningClick = useCallback(() => {
		const privileges: StoredPrivilege[] = JSON.parse(
			localStorage.getItem('LOGGED_IN_USER_PRIVILEGES') ?? '[]'
		) as StoredPrivilege[];

		const privilegeCodes = privileges.map((p) => p.privilege.code);

		const showCreateBatch = privilegeCodes.includes(
			PrivilegeCodes.CREATE_BATCH
		);

		const showUpdateBatch = privilegeCodes.includes(
			PrivilegeCodes.UPDATE_BATCH
		);

		const showEligibleCandidates = privilegeCodes.includes(
			PrivilegeCodes.GET_ELIGIBLE_CANDIDATES_FOR_NEXT_BATCH
		);

		const showBatchesPage =
			showCreateBatch || showUpdateBatch || showEligibleCandidates;
		const navigateTo = showBatchesPage || isAdmin;

		navigateWithGroupTab(navigateTo ? BATCH_HOME : FUNCTIONARY_HOME);
	}, [navigateWithGroupTab, isAdmin]);

	const handleNewPreceptorSupportClick = () =>
		navigateWithGroupTab(GROUPS, GroupTabs.CREATE_GROUP);

	const handleManageFunctionary = () =>
		navigateWithGroupTab(VIEW_FUNCTIONARIES);

	const handleManagePrivileges = () => navigateWithGroupTab(MODULES_LIST);

	const handlePreWorkClick = () =>
		navigateWithPreworkBatchIdAndGroupTab(
			PREWORKGROUPS,
			PreworkGroupTabs.CREATE_GROUP
		);

	const handleExamModuleClick = () => navigateWithGroupTab(EXAM_MODULE_HOME);

	const handleSwitchRoleClick = () => navigateToRoute(ROOT);

	const handleLogoutClick = useCallback(() => {
		if (logout) void logout();
	}, [logout]);

	/* ICON MAPPING */
	const MODULE_ICONS = {
		[RoleBasedModules.SCREENING_MODULE]: (
			<FileSearchOutlined className="text-2xl text-primary-blue" />
		),
		[RoleBasedModules.EXAM_MODULE]: (
			<FormOutlined className="text-2xl text-green" />
		),
		[RoleBasedModules.NPS_MODULE]: (
			<UserAddOutlined className="text-2xl text-purple" />
		),
		[RoleBasedModules.PREWORK_MODULE]: (
			<SettingOutlined className="text-2xl text-orange" />
		),
		[RoleBasedModules.MANAGE_FUNCTIONARIES_MODULE]: (
			<TeamOutlined className="text-2xl text-red" />
		),
		[RoleBasedModules.MANAGE_PRIVILEGES_MODULE]: (
			<SafetyOutlined className="text-2xl text-light-green" />
		),
	} as const;

	const MODULE_CLICK_HANDLERS = {
		[RoleBasedModules.SCREENING_MODULE]: handleScreeningClick,
		[RoleBasedModules.EXAM_MODULE]: handleExamModuleClick,
		[RoleBasedModules.NPS_MODULE]: handleNewPreceptorSupportClick,
		[RoleBasedModules.PREWORK_MODULE]: handlePreWorkClick,
		[RoleBasedModules.MANAGE_FUNCTIONARIES_MODULE]: handleManageFunctionary,
		[RoleBasedModules.MANAGE_PRIVILEGES_MODULE]: handleManagePrivileges,
	} as const;

	const loggedInUserModules =
		allRolesWithModules?.find((item) => item?.role?.code === selectedRole)
			?.modules ?? [];

	const modules: Module[] = loggedInUserModules.map((item) => ({
		key: item.module.code,
		id: item.module.id,
		label: item.module.name,
		icon: MODULE_ICONS[item.module.code as RoleBasedModules],
		onClick: () =>
			handleModuleClickWithPrivileges(
				item.module.id,
				MODULE_CLICK_HANDLERS[item.module.code as RoleBasedModules]
			),
	}));

	const roleData = allRolesWithModules?.find(
		(item) => item?.role?.code === selectedRole
	);

	const activeModules =
		roleData?.modules?.filter((m) => m.roleModuleMappingStatus === 'ACTIVE') ??
		[];

	const noModules = !roleData || activeModules.length === 0;

	return (
		<Layout className="bg-bground-blue min-h-screen flex items-center justify-center py-12">
			<div className="bg-white rounded-2xl shadow-md w-full max-w-6xl p-10">
				{/* Title */}
				<div className="text-center mb-8">
					<div>
						<span className="text-sm text-gray-500 pr-1">Current Role:</span>

						<span className="gap-1 bg-approve-blue text-primary-blue border border-approve-border-blue border-solid px-3 py-1 rounded-full font-semibold text-lg">
							<UserOutlined className="pr-1" />
							{roleLabel}
						</span>
					</div>

					{noModules ? (
						<h1 className="text-xl font-semibold text-gray-700 mt-16">
							{t('noModulesMapped')}{' '}
							<span
								className="text-primary-blue underline cursor-pointer font-semibold"
								onClick={handleSwitchRoleClick}
							>
								{t('switchRole')}
							</span>
							.
						</h1>
					) : (
						<>
							<h1 className="text-3xl font-bold">{t('pleaseSelectAModule')}</h1>

							<a
								className="underline text-primary-blue font-semibold text-base ml-2"
								onClick={handleSwitchRoleClick}
							>
								{t('switchRole')}
							</a>
						</>
					)}
				</div>

				{/* Module Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
					{modules.map(({ key, id, label, icon, onClick }) => (
						<Card
							key={key}
							hoverable
							className="cursor-pointer rounded-xl border border-gray-200 hover:border-approve-border-blue transition relative"
							onClick={onClick}
						>
							<Spin spinning={loadingModule === id}>
								<div className="flex items-center gap-4 min-h-[64px]">
									<div className="bg-gray-100 p-3 rounded-lg">{icon}</div>

									<span className="font-semibold text-lg">{label}</span>
								</div>
							</Spin>
						</Card>
					))}
				</div>

				{/* Logout */}
				<div className="text-center mt-10">
					<Button
						icon={
							<Icon src={LogoutImage} className="w-4 h-4 flex items-center" />
						}
						type="text"
						danger
						className="font-sans font-semibold text-base h-[50px] hover:py-4 text-red-600 hover:text-red-700"
						onClick={handleLogoutClick}
					>
						{t('logout')}
					</Button>
				</div>
			</div>
		</Layout>
	);
};
